// Which modules the ACTIVE business holds, whether it has set field service up
// (PRD 88 §4.1) or personal services up (PRD 121 §4.2), and — for cannabis (PRD 111 §4.1) — its restricted industry
// and the member's role: for the sidebar, which cannot ask BusinessContext.
//
// WHY A MODULE-LEVEL STORE: WebNavigation is mounted in AppShell, the PARENT
// route of BusinessShell, where BusinessProvider lives, so no context that
// provider owns can reach the sidebar. businessExperienceStore solved the same
// problem for the experience; this is that pattern for the modules. A row that
// needs a module (NavItemConfig.requiresModule) reads it here.
//
// UNKNOWN MEANS DO NOT DRAW — the opposite of the experience's rule, and on
// purpose. The experience SUBTRACTS rows a customer already has, so guessing
// wrong would hide them; a module row ADDS something most businesses do not
// hold, so guessing wrong would offer a paid, unreleased feature to everyone.
//
// BusinessContext publishes (it owns "which business"). The field service
// Overview also writes the set-up answer the moment set-up succeeds, so the
// sidebar and the Administration tile do not wait for the next page load.

import { BusinessModules, BusinessRole } from '../types/businessApi';
import type { RestrictedIndustry } from '../types/restrictedIndustry';

export interface BusinessModulesState {
  /** The business these answers are about; null while none is active. */
  businessId: string | null;
  /** What it holds today; null while unknown. */
  modules: BusinessModules | null;
  /**
   * Has it EVER set field service up? True through a lapse (the row is never
   * deleted). Null while unknown — not yet asked, the read failed, or nobody
   * asked because nothing would draw differently for the answer.
   */
  fieldServiceSetUp: boolean | null;
  /**
   * Has it EVER set personal services up (PRD 121 §4.2)? The same rule as
   * field service's: true through a lapse, null while unknown.
   */
  personalServicesSetUp: boolean | null;
  /**
   * The restricted industry the business is in (PRD 111 §4.12) — written only
   * by the cannabis module's set-up and never cleared on lapse, so for
   * cannabis it means "set up, possibly lapsed". Null while unknown or none.
   */
  restrictedIndustry: RestrictedIndustry | null;
  /** The member's role in the business; null while unknown. */
  role: BusinessRole | null;
}

/** What the business payload says beside its modules, for rules that need it. */
export interface BusinessModulesContext {
  restrictedIndustry?: RestrictedIndustry | null;
  role?: BusinessRole | null;
}

/**
 * How much of a module's sidebar group a business sees (PRD 88 §4.1):
 *   'none'       — nothing, which is also the answer while anything is unknown;
 *   'front_door' — only the module's own front door (its group and Overview),
 *                  whose page carries the banner;
 *   'all'        — every row: the module is held, or was set up before a lapse
 *                  (PRD 42 Rule 4: read-only, never invisible).
 */
export type ModuleNavAccess = 'none' | 'front_door' | 'all';

type Listener = (state: BusinessModulesState) => void;

const EMPTY: BusinessModulesState = {
  businessId: null, modules: null, fieldServiceSetUp: null, personalServicesSetUp: null,
  restrictedIndustry: null, role: null,
};

let state: BusinessModulesState = EMPTY;
let listeners = new Set<Listener>();

const publish = (next: BusinessModulesState) => {
  state = next;
  listeners.forEach(listener => listener(state));
};

export function getBusinessModulesState(): BusinessModulesState {
  return state;
}

/** Subscribe to changes; returns the unsubscribe function. */
export function subscribeBusinessModules(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Publish the active business's modules. A different business starts with its
 * set-up state unknown, so the outgoing business's answer is never drawn
 * against the incoming one; the same business keeps what it knew.
 */
export function setBusinessModules(
  businessId: string,
  modules: BusinessModules | null,
  context: BusinessModulesContext = {}
): void {
  const sameBusiness = state.businessId === businessId;
  const restrictedIndustry = context.restrictedIndustry ?? null;
  const role = context.role ?? null;
  if (sameBusiness && state.modules === modules && state.restrictedIndustry === restrictedIndustry &&
      state.role === role) return;
  publish({
    businessId,
    modules,
    fieldServiceSetUp: sameBusiness ? state.fieldServiceSetUp : null,
    personalServicesSetUp: sameBusiness ? state.personalServicesSetUp : null,
    restrictedIndustry,
    role,
  });
}

/**
 * Record whether `businessId` has set field service up. Ignored when another
 * business is active by now, so a slow answer for the business just left can
 * never land on the one just opened.
 */
export function setFieldServiceSetUp(businessId: string, setUp: boolean | null): void {
  if (state.businessId !== businessId || state.fieldServiceSetUp === setUp) return;
  publish({ ...state, fieldServiceSetUp: setUp });
}

/** Record whether `businessId` has set personal services up — setFieldServiceSetUp's rule. */
export function setPersonalServicesSetUp(businessId: string, setUp: boolean | null): void {
  if (state.businessId !== businessId || state.personalServicesSetUp === setUp) return;
  publish({ ...state, personalServicesSetUp: setUp });
}

/** Back to unknown: on leaving the business section and before a switch's first answer. */
export function clearBusinessModules(): void {
  if (state === EMPTY) return;
  publish(EMPTY);
}

/** Test hook — drops listeners and state. */
export function resetBusinessModulesStore(): void {
  state = EMPTY;
  listeners = new Set();
}
